import { api } from "@/utils/api";
import { socketService } from "@/utils/socket";
import { WSEvent } from "@shared/constants";
import type {
  Favorite,
  FavoriteWithResult,
  FavoriteStats,
  WSEventPayload,
} from "@shared/types";
import { defineStore } from "pinia";
import { computed, ref } from "vue";

export const useFavoritesStore = defineStore("favorites", () => {
  // State
  const favorites = ref<FavoriteWithResult[]>([]);
  const stats = ref<FavoriteStats>({
    totalFavorites: 0,
    addedToday: 0,
    addedThisWeek: 0,
  });
  const isLoading = ref(false);
  const favoriteResultIds = ref<Set<number>>(new Set());

  // Getters
  const totalFavorites = computed(() => favorites.value.length);

  const isFavorite = computed(() => (resultId: number) => {
    return favoriteResultIds.value.has(resultId);
  });

  // Actions
  const fetchFavorites = async (): Promise<void> => {
    isLoading.value = true;
    try {
      const response = await api.get<FavoriteWithResult[]>("/api/favorites");
      favorites.value = response;
      
      // Update quick lookup set
      favoriteResultIds.value = new Set(response.map((f) => f.resultId));
    } catch (error: unknown) {
      console.error("Failed to fetch favorites:", error);
      throw error;
    } finally {
      isLoading.value = false;
    }
  };

  const fetchStats = async (): Promise<void> => {
    try {
      const response = await api.get<FavoriteStats>("/api/favorites/stats");
      stats.value = response;
    } catch (error: unknown) {
      console.error("Failed to fetch favorite stats:", error);
    }
  };

  const addFavorite = async (resultId: number): Promise<void> => {
    try {
      const response = await api.post<Favorite>("/api/favorites", { resultId });
      
      // Optimistic update
      favoriteResultIds.value.add(resultId);
      
      // Refresh full list to get result data
      await fetchFavorites();
      await fetchStats();
    } catch (error: unknown) {
      // Rollback optimistic update
      favoriteResultIds.value.delete(resultId);
      console.error("Failed to add favorite:", error);
      throw error;
    }
  };

  const removeFavorite = async (resultId: number): Promise<void> => {
    try {
      await api.delete(`/api/favorites/${resultId}`);
      
      // Optimistic update
      favoriteResultIds.value.delete(resultId);
      favorites.value = favorites.value.filter((f) => f.resultId !== resultId);
      
      await fetchStats();
    } catch (error: unknown) {
      // Rollback optimistic update
      favoriteResultIds.value.add(resultId);
      console.error("Failed to remove favorite:", error);
      throw error;
    }
  };

  const toggleFavorite = async (resultId: number): Promise<void> => {
    if (isFavorite.value(resultId)) {
      await removeFavorite(resultId);
    } else {
      await addFavorite(resultId);
    }
  };

  const clearAllFavorites = async (): Promise<void> => {
    try {
      await api.delete("/api/favorites");
      
      favorites.value = [];
      favoriteResultIds.value.clear();
      stats.value = {
        totalFavorites: 0,
        addedToday: 0,
        addedThisWeek: 0,
      };
    } catch (error: unknown) {
      console.error("Failed to clear favorites:", error);
      throw error;
    }
  };

  // WebSocket Listeners
  const setupWebSocketListeners = () => {
    const socket = socketService.getSocket();
    if (!socket) return;

    socket.on(
      WSEvent.FAVORITE_ADDED,
      (payload: WSEventPayload[WSEvent.FAVORITE_ADDED]) => {
        favoriteResultIds.value.add(payload.resultId);
        fetchFavorites(); // Refresh to get full data
        fetchStats();
      },
    );

    socket.on(
      WSEvent.FAVORITE_REMOVED,
      (payload: WSEventPayload[WSEvent.FAVORITE_REMOVED]) => {
        favoriteResultIds.value.delete(payload.resultId);
        favorites.value = favorites.value.filter(
          (f) => f.resultId !== payload.resultId,
        );
        fetchStats();
      },
    );

    socket.on(
      WSEvent.FAVORITES_CLEARED,
      (payload: WSEventPayload[WSEvent.FAVORITES_CLEARED]) => {
        favorites.value = [];
        favoriteResultIds.value.clear();
        stats.value = {
          totalFavorites: 0,
          addedToday: 0,
          addedThisWeek: 0,
        };
      },
    );
  };

  return {
    // State
    favorites,
    stats,
    isLoading,
    favoriteResultIds,

    // Getters
    totalFavorites,
    isFavorite,

    // Actions
    fetchFavorites,
    fetchStats,
    addFavorite,
    removeFavorite,
    toggleFavorite,
    clearAllFavorites,
    setupWebSocketListeners,
  };
});
